import { ThemeContext } from "contexts";
import React, { useContext, useState } from "react";
import NormalText from "./NormalText";

type SelectModelProps = {
  imageUrls: string[];
  allowUpload?: boolean;
  allowUploadSetScreen?: (() => void) | null;
  handleSaveImageUrlOrBlob: (imageUrlOrBlob: string | Blob) => void;
};

const SelectModel: React.FC<SelectModelProps> = ({
  imageUrls,
  allowUpload = true,
  allowUploadSetScreen = null,
  handleSaveImageUrlOrBlob,
}) => {
  const theme = useContext(ThemeContext);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [errorUploadImageMessage, setErrorUploadImageMessage] = useState<
    string | null
  >(null);

  const handleImageClick = (index: number) => {
    setSelectedIndex(index);
    handleSaveImageUrlOrBlob(imageUrls[index]);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setErrorUploadImageMessage(null);
    const file = event.target.files?.[0];

    if (file) {
      const allowedExtensions = /(\.png|\.jpeg|\.jpg)$/i;
      if (!allowedExtensions.exec(file.name)) {
        setErrorUploadImageMessage("Please upload a PNG, JPEG, or JPG file.");
        return;
      }
      const blob = new Blob([file], { type: file.type });
      if (!blob) {
        setErrorUploadImageMessage("Failed to upload image.");
        return;
      }
      setPhotoBlob(blob);
      handleSaveImageUrlOrBlob(blob);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        {allowUploadSetScreen && (
          <div
            style={styles.uploadContainer}
            onClick={(e) => {
              e.preventDefault();
              if (allowUploadSetScreen) allowUploadSetScreen();
            }}
          >
            <div style={styles.uploadBox}>
              <div style={styles.uploadPlaceholder}>
                {/* TODO: Move the asset to another file */}
                <svg
                  width="36"
                  height="37"
                  viewBox="0 0 36 37"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_6845_4860)">
                    <path
                      d="M7.12598 21.4588C8.76704 19.262 11.3879 17.8398 14.3409 17.8398C16.4431 17.8398 18.3769 18.5606 19.9088 19.7684"
                      stroke="#868B97"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.0548 27.4818H4.05481C3.37285 27.4818 2.71878 27.2108 2.23655 26.7287C1.75432 26.2465 1.4834 25.5924 1.4834 24.9104V4.33902C1.4834 3.65702 1.75432 3.00298 2.23655 2.52073C2.71878 2.0385 3.37285 1.76758 4.05481 1.76758H24.6262C25.3082 1.76758 25.9622 2.0385 26.4446 2.52073C26.9268 3.00298 27.1978 3.65702 27.1978 4.33902V12.0533"
                      stroke="#868B97"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14.3405 13.9819C16.4708 13.9819 18.1977 12.2549 18.1977 10.1247C18.1977 7.99447 16.4708 6.26758 14.3405 6.26758C12.2103 6.26758 10.4834 7.99447 10.4834 10.1247C10.4834 12.2549 12.2103 13.9819 14.3405 13.9819Z"
                      stroke="#868B97"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.8741 27.3127C17.9717 27.1556 17.9717 25.8602 18.8741 25.7032C22.1433 25.1344 24.7435 22.6443 25.4531 19.4028L25.5074 19.1543C25.7027 18.2625 26.9726 18.2569 27.1755 19.147L27.2416 19.4366C27.9776 22.6628 30.5783 25.1319 33.8386 25.6991C34.7456 25.8568 34.7456 27.1589 33.8386 27.3168C30.5783 27.8838 27.9776 30.3529 27.2416 33.5793L27.1755 33.8688C26.9726 34.7588 25.7027 34.7534 25.5074 33.8613L25.4531 33.6129C24.7435 30.3714 22.1433 27.8812 18.8741 27.3127Z"
                      stroke="#868B97"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_6845_4860">
                      <rect
                        width="36"
                        height="36"
                        fill="white"
                        transform="translate(0 0.150391)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <p style={styles.uploadText}>Upload your own</p>
              </div>
            </div>
          </div>
        )}
        {allowUpload && (
          <div style={styles.uploadContainer}>
            <div style={styles.uploadBox}>
              <label htmlFor="file-upload">
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={styles.fileInput}
                />
                {errorUploadImageMessage ? (
                  <NormalText
                    textContent={errorUploadImageMessage}
                    style={styles.errorText}
                  />
                ) : photoBlob ? (
                  <img
                    src={URL.createObjectURL(photoBlob)}
                    alt=""
                    style={styles.photo}
                  />
                ) : (
                  <div style={styles.uploadPlaceholder}>
                    {/* TODO: Move the asset to another file */}
                    <svg
                      width="36"
                      height="37"
                      viewBox="0 0 36 37"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_6845_4860)">
                        <path
                          d="M7.12598 21.4588C8.76704 19.262 11.3879 17.8398 14.3409 17.8398C16.4431 17.8398 18.3769 18.5606 19.9088 19.7684"
                          stroke="#868B97"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.0548 27.4818H4.05481C3.37285 27.4818 2.71878 27.2108 2.23655 26.7287C1.75432 26.2465 1.4834 25.5924 1.4834 24.9104V4.33902C1.4834 3.65702 1.75432 3.00298 2.23655 2.52073C2.71878 2.0385 3.37285 1.76758 4.05481 1.76758H24.6262C25.3082 1.76758 25.9622 2.0385 26.4446 2.52073C26.9268 3.00298 27.1978 3.65702 27.1978 4.33902V12.0533"
                          stroke="#868B97"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14.3405 13.9819C16.4708 13.9819 18.1977 12.2549 18.1977 10.1247C18.1977 7.99447 16.4708 6.26758 14.3405 6.26758C12.2103 6.26758 10.4834 7.99447 10.4834 10.1247C10.4834 12.2549 12.2103 13.9819 14.3405 13.9819Z"
                          stroke="#868B97"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18.8741 27.3127C17.9717 27.1556 17.9717 25.8602 18.8741 25.7032C22.1433 25.1344 24.7435 22.6443 25.4531 19.4028L25.5074 19.1543C25.7027 18.2625 26.9726 18.2569 27.1755 19.147L27.2416 19.4366C27.9776 22.6628 30.5783 25.1319 33.8386 25.6991C34.7456 25.8568 34.7456 27.1589 33.8386 27.3168C30.5783 27.8838 27.9776 30.3529 27.2416 33.5793L27.1755 33.8688C26.9726 34.7588 25.7027 34.7534 25.5074 33.8613L25.4531 33.6129C24.7435 30.3714 22.1433 27.8812 18.8741 27.3127Z"
                          stroke="#868B97"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_6845_4860">
                          <rect
                            width="36"
                            height="36"
                            fill="white"
                            transform="translate(0 0.150391)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <p style={styles.uploadText}>Upload your own</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        )}
        {imageUrls.map((imageUrl, index) => (
          <div
            key={index}
            onClick={() => handleImageClick(index)}
            style={{
              ...styles.imageContainer,
              border:
                index === selectedIndex
                  ? `2px solid ${theme?.themeColor ?? "#00B7F4"}`
                  : "2px solid transparent",
            }}
          >
            <img src={imageUrl} alt="" style={styles.image} />
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    overflow: "auto",
    maxHeight: "100%",
    width: "100%",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    width: "100%",
  },
  uploadContainer: {
    padding: "4px",
    boxSizing: "border-box",
    width: "100%",
    height: "100%",
    margin: "auto",
  },
  uploadBox: {
    position: "relative",
    border: "2px solid #D3D3D3",
    borderRadius: "16px",
    padding: "4px",
    cursor: "pointer",
    aspectRatio: "150 / 200",
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  fileInput: {
    position: "absolute",
    inset: 0,
    opacity: 0,
    cursor: "pointer",
    width: "100%",
    height: "100%",
  },
  errorText: {
    color: "#ff0000",
    textAlign: "center",
  },
  photo: {
    width: "calc(100% - 4px)",
    height: "calc(100% - 4px)",
    aspectRatio: "1 / 1",
    objectFit: "cover",
  },
  uploadPlaceholder: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  uploadText: {
    color: "#868B97",
    fontWeight: "500",
  },
  imageContainer: {
    padding: "4px",
    cursor: "pointer",
    aspectRatio: "150 / 200",
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "fill",
  },
} as const;

export default SelectModel;
